import React from "react";
import {
    HoldGroupSection,
    SimpleEventSeatmap,
    SimpleSeatmapSection,
} from "../../models/Event";
import { CartItem, GACartItem } from "../../models/Cart";
import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    Icon,
    IconButton,
    Typography,
} from "@mui/material";
import CartStore, { GACartItemKey } from "../../stores/CartStore";
import { capitalizeWords, priceFormatter } from "../../utils/helpers";
import TrashIcon from "@mui/icons-material/DeleteOutline";
import Remove from "@mui/icons-material/Remove";
import Add from "@mui/icons-material/Add";
import AuthStore from "../../stores/AuthStore";
import { removeCartSeatWithStrandGuard } from "../seatmap/cartStrandGuard";

type Props = {
    row: {
        eventSeatmap: SimpleEventSeatmap;
        seats: CartItem[];
        sections: {
            [key: number]: {
                section: SimpleSeatmapSection;
                items: CartItem[];
            };
        };
    };
    cartStore: CartStore | null;
};

const SeatRow = ({
    seat,
    cartStore,
}: {
    seat: CartItem;
    cartStore: CartStore | null;
}) => {
    const getPriceNode = (): React.ReactNode => {
        if (seat.comp) {
            return <Chip color="warning" label="COMP" size="small" />;
        }
        if (seat.overridePrice) {
            return (
                <Typography variant="body2">
                    {priceFormatter(seat.basePrice ?? seat.overridePrice)}
                </Typography>
            );
        }
        if ((seat.basePrice ?? seat.department.price) === 0) {
            return <Chip color="success" label="FREE" size="small" />;
        }
        // if (seat.redemption) {
        //     return (
        //         <Chip
        //             color="success"
        //             size="small"
        //             label={"Code: " + seat.redemption.code}
        //             onDelete={onRemoveRedemptionCode}
        //         />
        //     );
        // }
        return (
            <Typography variant="body2">
                {priceFormatter(seat.basePrice ?? seat.department.price)}
            </Typography>
        );
    };

    const getDiscountNode = (): React.ReactNode => {
        if (seat.comp) return null;
        if (seat.overridePrice !== null) {
            return (
                <Typography variant="body2" color="success.main">
                    {priceFormatter(seat.department.price - seat.overridePrice)}{" "}
                    in discounts
                </Typography>
            );
        }
        if (seat.discount) {
            return (
                <Typography variant="body2" color="success.main">
                    {priceFormatter(
                        seat.discount.grossAmount ?? seat.discount.amount,
                    )}{" "}
                    in discounts
                </Typography>
            );
        }
        return null;
    };

    const getServiceFeeNode = (): React.ReactNode => {
        if (seat.comp) return null;
        return seat.serviceFees.map((it) => (
            <Typography variant="body2" color="text.secondary" key={it.id}>
                + {priceFormatter(it.amount)} {it.name}
            </Typography>
        ));
    };


    return (
        <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Grid container spacing={1}>
                <Grid item xs={7}>
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        color="primaryText.main"
                    >
                        {seat.department.name}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                        {capitalizeWords(seat.sectionSeat?.seatDetails ?? "")}
                    </Typography>
                    {getPriceNode()}
                    {getServiceFeeNode()}
                    {getDiscountNode()}
                </Grid>
                <Grid
                    item
                    xs={2.5}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    {seat.acceptsModifications && (
                        <IconButton
                            color="error"
                            onClick={() => {
                                if (!cartStore) return;
                                const eventSeatmapId = seat.eventSeatmap!.id;
                                // Handing a seat back can strand its
                                // neighbour, so the same guard the seatmap
                                // applies runs here too.
                                void removeCartSeatWithStrandGuard({
                                    cartStore,
                                    source: {
                                        kind: "seatmap",
                                        eventSeatmapId,
                                    },
                                    seatId: seat.sectionSeat!.id,
                                    remove: (seatId) =>
                                        cartStore.removeSeatmapTicketFromCart(
                                            eventSeatmapId,
                                            seatId,
                                        ),
                                });
                            }}
                        >
                            <TrashIcon />
                        </IconButton>
                    )}
                </Grid>
                <Grid
                    item
                    xs={2.5}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                    }}
                >
                    <Typography variant="body2">
                        {priceFormatter(cartStore?.itemTotal(seat) ?? 0)}
                    </Typography>
                </Grid>
            </Grid>
        </Box>
    );
};

const SectionRow = ({
    section,
    items,
    cartStore,
}: {
    section: SimpleSeatmapSection;
    items: CartItem[];
    cartStore: CartStore | null;
}) => {
    const gaItems = Object.values(
        items.reduce((a, b) => {
            if (a[GACartItemKey(b)]) {
                a[GACartItemKey(b)].quantity++;
                a[GACartItemKey(b)].totalAmount += cartStore?.itemTotal(b) ?? 0;
                a[GACartItemKey(b)].ticketIds.push(b.ticketId);
            } else {
                a[GACartItemKey(b)] = {
                    event: b.event,
                    department: b.department,
                    comp: b.comp,
                    acceptsModifications: b.acceptsModifications,
                    ticketIds: [b.ticketId],
                    quantity: 1,
                    applicableTaxIds: b.applicableTaxIds,
                    serviceFees: b.serviceFees,
                    discount: b.discount,
                    redemption: b.redemption,
                    totalAmount: cartStore?.itemTotal(b) ?? 0,
                    basePrice: cartStore?.itemPrice(b) ?? 0,
                    taxInclusive: b.taxInclusive,
                    grossPrice: b.grossPrice,
                    taxes: b.taxes,
                    overridePrice: b.overridePrice,
                    eventSeatmap: b.eventSeatmap!,
                    seatmapSection: b.seatmapSection!,
                    holdGroupSection: b.holdGroupSection,
                };
            }
            return a;
        }, {} as { [key: string]: GACartItem & { eventSeatmap: SimpleEventSeatmap; seatmapSection: SimpleSeatmapSection; holdGroupSection: HoldGroupSection | null } }),
    );

    return (
        <>
            {gaItems.map((it, idx) => {
                const getPriceNode = (): React.ReactNode => {
                    if (it.comp) {
                        return (
                            <Chip color="warning" label="COMP" size="small" />
                        );
                    }
                    if (it.overridePrice) {
                        return (
                            <Typography variant="body2">
                                {priceFormatter(it.overridePrice)}
                            </Typography>
                        );
                    }
                    if (cartStore?.itemPrice(it) === 0) {
                        return (
                            <Chip color="success" label="FREE" size="small" />
                        );
                    }
                    // if (seat.redemption) {
                    //     return (
                    //         <Chip
                    //             color="success"
                    //             size="small"
                    //             label={"Code: " + seat.redemption.code}
                    //             onDelete={onRemoveRedemptionCode}
                    //         />
                    //     );
                    // }
                    return (
                        <Typography variant="body2">
                            {priceFormatter(
                                it.basePrice ?? it.department.price,
                            )}
                        </Typography>
                    );
                };

                const getDiscountNode = (): React.ReactNode => {
                    if (it.comp) return null;
                    if (it.overridePrice !== null) {
                        return (
                            <Typography variant="body2" color="success.main">
                                {priceFormatter(
                                    it.department.price - it.overridePrice,
                                )}{" "}
                                in discounts
                            </Typography>
                        );
                    }
                    if (it.discount) {
                        return (
                            <Typography variant="body2" color="success.main">
                                {priceFormatter(
                                    it.discount.grossAmount ??
                                        it.discount.amount,
                                )}{" "}
                                in discounts ea.
                            </Typography>
                        );
                    }
                    return null;
                };

                const getServiceFeeNode = (): React.ReactNode => {
                    if (it.comp) return null;
                    return it.serviceFees.map((it) => (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            key={it.id}
                        >
                            + {priceFormatter(it.amount)} {it.name}
                        </Typography>
                    ));
                };


                const onEnterCustomQuantity = () => {
                    const quantity = window.prompt(
                        "Enter custom quantity (1-300)",
                        it.quantity.toString(),
                    );
                    if (quantity) {
                        const parsedQuantity = parseInt(quantity);
                        if (parsedQuantity >= 1 && parsedQuantity <= 300) {
                            cartStore?.setSeatmapSectionQtyInCart(
                                it.eventSeatmap.id,
                                it.seatmapSection.id,
                                it.department.id,
                                parsedQuantity,
                                it.comp,
                                it.holdGroupSection?.id ?? undefined,
                                it.department,
                            );
                        } else {
                            alert("Please enter a number between 1 and 300");
                        }
                    }
                };

                return (
                    <Box
                        sx={{ display: "flex", flexDirection: "column" }}
                        key={idx}
                    >
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            color="primaryText.main"
                        >
                            {it.department.name}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                            {section.sectionDetails}
                        </Typography>
                        <Grid container spacing={1}>
                            <Grid item xs={7}>
                                {getPriceNode()}
                                {getServiceFeeNode()}
                                {getDiscountNode()}
                            </Grid>
                            <Grid
                                item
                                xs={2.5}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "5px",
                                }}
                            >
                                <IconButton
                                    color="error"
                                    onClick={() => {
                                        cartStore?.removeSeatmapSectionFromCart(
                                            it.eventSeatmap.id,
                                            it.seatmapSection.id,
                                            it.department.id,
                                            it.comp,
                                            it.holdGroupSection?.id ??
                                                undefined,
                                            it.redemption?.id,
                                            it.department,
                                        );
                                    }}
                                    size="small"
                                >
                                    <Remove fontSize="small" />
                                </IconButton>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        cursor: AuthStore.boxoffice
                                            ? "pointer"
                                            : "default",
                                    }}
                                    onClick={
                                        AuthStore.boxoffice
                                            ? onEnterCustomQuantity
                                            : undefined
                                    }
                                >
                                    {it.quantity}
                                </Typography>
                                <IconButton
                                    color="success"
                                    onClick={() => {
                                        cartStore?.addSeatmapSectionToCart(
                                            it.eventSeatmap.id,
                                            it.seatmapSection.id,
                                            it.department.id,
                                            it.comp,
                                            it.holdGroupSection?.id ??
                                                undefined,
                                            it.redemption?.id,
                                            it.department,
                                        );
                                    }}
                                    size="small"
                                >
                                    <Add fontSize="small" />
                                </IconButton>
                            </Grid>
                            <Grid
                                item
                                xs={2.5}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <Typography variant="body2">
                                    {priceFormatter(it.totalAmount)}
                                </Typography>
                            </Grid>
                        </Grid>
                        {idx !== gaItems.length - 1 && (
                            <Divider
                                sx={{
                                    my: 2,
                                }}
                            />
                        )}
                    </Box>
                );
            })}
        </>
    );
};

const SeatmapCartRow = ({ row, cartStore }: Props) => {
    return (
        <Card>
            <CardContent>
                <Typography
                    variant="body2"
                    color="primaryText.main"
                    fontWeight={600}
                >
                    {row.eventSeatmap.name}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                    {row.seats.map((it2, idx2) => (
                        <Grid item xs={12} key={it2.ticketId}>
                            <SeatRow seat={it2} cartStore={cartStore} />
                            {(idx2 !== row.seats.length - 1 ||
                                Object.values(row.sections).length > 0) && (
                                <Divider
                                    sx={{
                                        mt: 2,
                                    }}
                                />
                            )}
                        </Grid>
                    ))}
                    {Object.values(row.sections).map((it, idx) => (
                        <Grid item xs={12} key={idx}>
                            <SectionRow
                                section={it.section}
                                items={it.items}
                                cartStore={cartStore}
                            />
                        </Grid>
                    ))}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default SeatmapCartRow;
